import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FoundingSeatsCounter } from '../components/FoundingSeatsCounter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PayNowPage() {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(null);

  useEffect(() => {
    // Load Razorpay checkout script
    if (!document.querySelector('script[src*="razorpay"]')) {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);
    }

    const fetchInfo = async () => {
      try {
        // Prefer the real per-user subscribe quote (1-click Subscribe deep link)
        const qRes = await fetch(`${API_URL}/api/activation/subscribe-quote/${schoolId || 'default'}`);
        if (qRes.ok) {
          const q = await qRes.json();
          if (q.success) {
            setPaymentInfo({
              plan_name: q.plan_name,
              amount: q.amount,
              currency: q.currency || 'INR',
              status: 'due',
              razorpay_key: q.razorpay_key,
              billable_users: q.billable_users,
              per_user_rate: q.per_user_rate,
              is_founding: q.is_founding,
              founding_seats_left: q.founding_seats_left,
              currency_symbol: q.currency_symbol || '₹',
              school_name: q.school_name,
            });
            setLoading(false);
            return;
          }
        }
      } catch { /* fall through to legacy */ }
      try {
        const res = await fetch(`${API_URL}/api/payments/pay-link/${schoolId || 'default'}`);
        const data = await res.json();
        if (data.success) setPaymentInfo(data.payment_info);
      } catch (err) {
        console.error('Failed to load payment info:', err);
      }
      setLoading(false);
    };
    fetchInfo();
  }, [schoolId]);

  const handlePayNow = async () => {
    if (!paymentInfo?.razorpay_key) {
      alert('Payment system not configured');
      return;
    }
    setPaying(true);

    try {
      // Step 1: Create Razorpay Order via backend
      const orderRes = await fetch(`${API_URL}/api/razorpay/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: paymentInfo.amount * 100, // Convert INR to paise
          currency: paymentInfo.currency || 'INR',
          receipt: `pay_${schoolId || 'default'}_${Date.now()}`.slice(0, 40),
          notes: {
            school_id: schoolId || 'default',
            plan_name: paymentInfo.plan_name
          },
          school_id: schoolId || 'default',
          payment_type: 'subscription'
        })
      });
      const orderData = await orderRes.json();

      if (!orderData.success) {
        alert(orderData.detail || 'Failed to create order');
        setPaying(false);
        return;
      }

      const orderId = orderData.order?.id;
      const keyId = orderData.key_id || paymentInfo.razorpay_key;

      // Step 2: Open Razorpay Checkout with order_id
      const options = {
        key: keyId,
        amount: paymentInfo.amount * 100,
        currency: paymentInfo.currency || 'INR',
        name: 'System Of Success',
        description: `${paymentInfo.plan_name} - Shiksha Mitra`,
        order_id: orderId,
        handler: async function (response) {
          // Step 3: Verify payment signature via backend
          try {
            const verifyRes = await fetch(`${API_URL}/api/razorpay/verify-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                school_id: schoolId || 'default'
              })
            });
            const verifyData = await verifyRes.json();
            if (verifyData.success) {
              setPaymentSuccess({
                payment_id: response.razorpay_payment_id,
                order_id: response.razorpay_order_id
              });
            } else {
              setPaymentSuccess({
                payment_id: response.razorpay_payment_id,
                order_id: response.razorpay_order_id
              });
            }
          } catch {
            setPaymentSuccess({
              payment_id: response.razorpay_payment_id,
              order_id: response.razorpay_order_id
            });
          }
          setPaying(false);
        },
        prefill: { contact: '' },
        theme: { color: '#f97316' },
        modal: { ondismiss: () => setPaying(false) }
      };

      if (window.Razorpay) {
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function(resp) {
          setPaying(false);
          alert(`Payment failed: ${resp.error.description}`);
        });
        rzp.open();
      } else {
        alert('Payment gateway loading... Please try again.');
        setPaying(false);
      }
    } catch (err) {
      console.error('Payment error:', err);
      alert('Something went wrong. Please try again.');
      setPaying(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
    </div>
  );

  if (paymentSuccess) return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="payment-success">
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden text-center">
          <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-8 text-white">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold">Payment Successful!</h1>
            <p className="text-green-100 text-sm mt-1">Thank you for your payment</p>
          </div>
          <div className="p-6 space-y-4">
            <div className="bg-green-50 rounded-lg p-3 text-sm text-green-700">
              Payment ID: <span className="font-mono font-medium">{paymentSuccess.payment_id}</span>
            </div>
            {paymentSuccess.order_id && (
              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                Order ID: <span className="font-mono font-medium">{paymentSuccess.order_id}</span>
              </div>
            )}
            <button
              onClick={() => navigate(`/receipt/${paymentSuccess.order_id || paymentSuccess.payment_id}`)}
              className="w-full py-3 bg-orange-500 text-white rounded-xl font-bold hover:bg-orange-600 transition"
              data-testid="view-receipt-btn"
            >
              View Receipt
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="pay-now-page">
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold">Subscription Payment</h1>
                <p className="text-orange-100 text-sm">Shiksha Mitra - System Of Success</p>
              </div>
              <span className="text-4xl">💳</span>
            </div>
          </div>

          {/* Payment Details */}
          {paymentInfo && (
            <div className="p-6 space-y-4">
              {/* Live Founding seats counter */}
              {paymentInfo.is_founding && <FoundingSeatsCounter />}
              <div className="space-y-3">
                {[
                  { label: 'Plan', value: paymentInfo.plan_name },
                  { label: 'Status', value: paymentInfo.status, badge: true },
                  { label: 'Due Date', value: paymentInfo.due_date ? new Date(paymentInfo.due_date).toLocaleDateString('hi-IN') : 'As soon as possible' },
                ].filter(r => r.value).map((row, i) => (
                  <div key={i} className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">{row.label}</span>
                    {row.badge ? (
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium capitalize">{row.value}</span>
                    ) : (
                      <span className="text-sm font-medium text-gray-800">{row.value}</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Amount */}
              {paymentInfo.is_custom ? (
                <div className="bg-slate-900 text-white rounded-xl p-5 text-center" data-testid="pay-custom-box">
                  <p className="font-semibold text-amber-300 mb-1">🏛️ 5000+ students — Custom Pricing</p>
                  <p className="text-sm text-gray-300 mb-3">Please contact our sales team for the best plan.</p>
                  <a href={`https://wa.me/91${paymentInfo.sales_contact_phone || '9826618603'}?text=${encodeURIComponent(`Hi! Our school ${paymentInfo.school_name || ''} has ${paymentInfo.student_count} students. Please share Shiksha Mitra custom pricing.`)}`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-500 hover:bg-green-600 rounded-lg font-bold text-white transition" data-testid="pay-contact-sales">
                    <i className="fa-brands fa-whatsapp"></i> Contact Sales
                  </a>
                </div>
              ) : (
                <>
                <div className="bg-orange-50 rounded-xl p-4 text-center">
                  {paymentInfo.pricing_tier && (
                    <span className="inline-block mb-2 px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[11px] font-bold" data-testid="pay-tier-label">{paymentInfo.pricing_tier}</span>
                  )}
                  <p className="text-sm text-gray-500 mb-1">Amount Due (One-time + 1yr Maintenance)</p>
                  <p className="text-3xl font-bold text-gray-900" data-testid="pay-amount">
                    ₹{paymentInfo.amount?.toLocaleString('en-IN')}
                  </p>
                  {paymentInfo.license_amount != null && (
                    <p className="text-xs text-gray-500 mt-1" data-testid="pay-breakdown">
                      ₹{paymentInfo.license_amount?.toLocaleString('en-IN')} one-time license + ₹{paymentInfo.annual_maintenance?.toLocaleString('en-IN')} maintenance/year
                    </p>
                  )}
                </div>

                {/* Pay Button */}
                <button onClick={handlePayNow} disabled={paying} data-testid="razorpay-pay-btn"
                  className="w-full py-4 bg-orange-500 text-white rounded-xl font-bold text-lg hover:bg-orange-600 disabled:opacity-50 transition shadow-lg shadow-orange-200">
                  {paying ? 'Processing...' : `Pay ₹${paymentInfo.amount?.toLocaleString('en-IN')} Now`}
                </button>
                </>
              )}

              <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
                <span>Secured by</span>
                <span className="font-medium text-gray-600">Razorpay</span>
                <span>|</span>
                <span>100% Safe</span>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="border-t px-6 py-4 bg-gray-50 text-center">
            <p className="text-xs text-gray-400">System Of Success | shiksha-mitra.com</p>
          </div>
        </div>
      </div>
    </div>
  );
}
